/** @typedef {import("../widgets/BaseWidget").BaseWidget} BaseWidget */

/**
 * Merge a fresh Firestore snapshot into the editor's in-memory widgets
 * without discarding changes that haven't been written yet.
 *
 * Every snapshot deserialises NEW widget objects. A snapshot that lands while
 * an edit or delete is still pending — an earlier save still in flight, or the
 * scheduleSave debounce still waiting — would otherwise replace the object
 * holding that change, and the next save would find nothing to write.
 *
 * Keeps the local instance when it:
 *  - is dirty (an edit not yet serialised by saveWidgets);
 *  - is deleted locally while the snapshot still has it;
 *  - has no docid and isn't in the snapshot (new, not yet written).
 * Everything else comes from the snapshot, in snapshot order.
 *
 * Must keep every pending change that widgetsNeedingSave would write, or the
 * snapshot discards it again — pinned by the '↔ widgetsNeedingSave' test.
 *
 * AI-CONTEXT: See docs/pitfalls.md#state-snapshot-replaces-unsaved-widget
 *
 * @param {BaseWidget[]} current Widgets currently held by the editor
 * @param {BaseWidget[]} incoming Widgets just deserialised from a snapshot
 * @returns {BaseWidget[]}
 */
const mergeSnapshotWidgets = (current, incoming) => {
    const currentById = new Map(current.map(w => [w.id, w]));
    const incomingIds = new Set(incoming.map(w => w.id));

    const merged = incoming.map(widget => {
        const local = currentById.get(widget.id);
        if (local && (local.isDirty || (local.deleted && !widget.deleted))) {
            return local;
        }
        return widget;
    });

    const unsaved = current.filter(w => !w.docid && !w.deleted && !incomingIds.has(w.id));

    return [...merged, ...unsaved];
};

export { mergeSnapshotWidgets };
